import {
  CopyOutlined,
  DeleteOutlined,
  DisconnectOutlined,
  EditOutlined,
  ExperimentOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Collapse,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { AppLayout } from '../components/AppLayout';
import { PageHead } from '../components/PageHead';
import { SlideConnectionsModal } from '../components/SlideConnectionsModal';
import { SlideTestPanel } from '../components/SlideTestPanel';
import { api, type SlideLicenseRow } from '../lib/api';
import {
  SLIDE_CONTROL_DOWNLOADS,
  SLIDE_CONTROL_DOWNLOAD_PAGE,
} from '../lib/slideControlDownloads';

const { Title, Text, Paragraph } = Typography;

async function copy(text: string, label: string) {
  await navigator.clipboard.writeText(text);
  message.success(`Copied ${label}`);
}

function KeyCell({ value }: { value: string }) {
  return (
    <Space size="small">
      <code style={{ fontSize: 12 }}>{value}</code>
      <Button
        size="small"
        type="text"
        icon={<CopyOutlined />}
        onClick={() => copy(value, 'license key')}
      />
    </Space>
  );
}

export function SlideLicensesScene() {
  const [rows, setRows] = useState<SlideLicenseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<SlideLicenseRow | null>(null);
  const [testing, setTesting] = useState<SlideLicenseRow | null>(null);
  const [connectionsOf, setConnectionsOf] = useState<SlideLicenseRow | null>(null);
  const [form] = Form.useForm<{ name: string }>();

  const load = async () => {
    setLoading(true);
    try {
      setRows(await api<SlideLicenseRow[]>('/api/slide-licenses'));
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to load licenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setCreateOpen(true);
  };

  const openEdit = (row: SlideLicenseRow) => {
    setEditing(row);
    form.setFieldsValue({ name: row.name });
    setCreateOpen(true);
  };

  const onSave = async ({ name }: { name: string }) => {
    try {
      if (editing) {
        await api(`/api/slide-licenses/${editing.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ name }),
        });
      } else {
        await api('/api/slide-licenses', {
          method: 'POST',
          body: JSON.stringify({ name }),
        });
      }
      setCreateOpen(false);
      await load();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Save failed');
    }
  };

  const rotate = async (row: SlideLicenseRow) => {
    try {
      await api(`/api/slide-licenses/${row.id}/rotate-key`, { method: 'POST' });
      await load();
      message.success(`Generated a new key for ${row.name}`);
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Rotate failed');
    }
  };

  const remove = async (row: SlideLicenseRow) => {
    try {
      await api(`/api/slide-licenses/${row.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Delete failed');
    }
  };

  const columns: ColumnsType<SlideLicenseRow> = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Session key',
      dataIndex: 'key',
      render: (k: string) => <KeyCell value={k} />,
    },
    {
      title: 'Added',
      dataIndex: 'created_at',
      width: 160,
      render: (ts: number) => dayjs(ts).format('D MMM YYYY'),
    },
    {
      title: '',
      key: 'actions',
      width: 420,
      render: (_t, row) => (
        <Space>
          <Button
            size="small"
            type="primary"
            icon={<ExperimentOutlined />}
            onClick={() => setTesting(row)}
          >
            Test
          </Button>
          <Button
            size="small"
            icon={<DisconnectOutlined />}
            onClick={() => setConnectionsOf(row)}
          >
            Connections
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(row)}
          >
            Rename
          </Button>
          <Popconfirm
            title="Rotate session key?"
            description="The Slide Control app on that machine will need to be re-pasted with the new key. Use only if the old one was leaked."
            okText="Rotate"
            onConfirm={() => rotate(row)}
          >
            <Button size="small" icon={<ReloadOutlined />}>
              Rotate
            </Button>
          </Popconfirm>
          <Popconfirm
            title={`Delete ${row.name}?`}
            description="Any event speaker pointing at this license will have slide control switched off."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => remove(row)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AppLayout>
      <PageHead title="Slide licenses" />
      <Space
        align="center"
        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Slide licenses
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          New license
        </Button>
      </Space>
      <Paragraph type="secondary">
        One license per physical machine — Windows PC or Mac — running the
        AOTV Slide Control app. The session key is persistent: paste it into
        the app once and it remembers. You can re-use a license across many
        speakers (e.g. a panel where everyone shares the same deck on the same
        laptop).
      </Paragraph>

      <Collapse
        style={{ marginBottom: 16 }}
        items={[
          {
            key: 'install',
            label: 'How to install the AOTV Slide Control app (Windows or Mac)',
            children: (
              <>
                <Paragraph style={{ marginBottom: 8 }}>
                  Each slides machine needs the app installed once. It drives
                  PowerPoint (and Keynote on a Mac) and streams presenter
                  notes back to the speaker's browser.
                </Paragraph>
                <Paragraph style={{ marginBottom: 12 }}>
                  <Text strong>Download:</Text>{' '}
                  <a
                    href={SLIDE_CONTROL_DOWNLOADS.windows}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Windows
                  </a>
                  {' · '}
                  <a
                    href={SLIDE_CONTROL_DOWNLOADS.mac}
                    target="_blank"
                    rel="noreferrer"
                  >
                    macOS
                  </a>
                  {' — or send an operator the public install page: '}
                  <a
                    href={SLIDE_CONTROL_DOWNLOAD_PAGE}
                    target="_blank"
                    rel="noreferrer"
                  >
                    call.aotv.co.uk/downloads
                  </a>
                  {' (no login needed).'}
                </Paragraph>
                <Paragraph style={{ marginBottom: 4 }}>
                  <Text strong>Windows</Text>
                </Paragraph>
                <ol style={{ paddingLeft: 20, marginBottom: 12 }}>
                  <li>
                    Unzip and run <code>Install.cmd</code> — per-user install,
                    no admin password.
                  </li>
                  <li>
                    First launch only: SmartScreen may warn. Click{' '}
                    <Text strong>More info</Text> →{' '}
                    <Text strong>Run anyway</Text>.
                  </li>
                  <li>
                    Click the AOTV icon by the clock, paste the session key
                    from the table below, and click{' '}
                    <Text strong>Connect</Text>.
                  </li>
                  <li>
                    Leave <Text strong>Mode</Text> on PowerPoint, or switch to{' '}
                    <Text strong>Generic</Text> for Google Slides, Canva and
                    web decks.
                  </li>
                </ol>
                <Paragraph style={{ marginBottom: 4 }}>
                  <Text strong>macOS</Text>
                </Paragraph>
                <ol style={{ paddingLeft: 20, marginBottom: 8 }}>
                  <li>
                    Unzip and drag <code>AOTV Slide Control.app</code> into{' '}
                    <code>/Applications</code>.
                  </li>
                  <li>
                    First launch only: macOS Gatekeeper may block it.
                    Right-click the app in Finder → <Text strong>Open</Text> →{' '}
                    <Text strong>Open</Text>.
                  </li>
                  <li>
                    Click the menu-bar icon, paste the session key, and click{' '}
                    <Text strong>Connect</Text>.
                  </li>
                  <li>
                    Grant the permissions the popover prompts for —{' '}
                    <Text strong>Automation</Text> for Keynote and PowerPoint,
                    plus <Text strong>Accessibility</Text>. Without these,
                    slide advance and notes reading silently fail mid-show.
                  </li>
                </ol>
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  Both apps self-update in the background. Use{' '}
                  <Text code>Check for Updates…</Text> from the ellipsis menu
                  to pull a newer build on demand.
                </Paragraph>
              </>
            ),
          },
        ]}
      />

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          locale={{
            emptyText:
              'No licenses yet. Create one per slides machine you run.',
          }}
        />
      </Card>

      <Modal
        title={editing ? `Rename ${editing.name}` : 'New slide license'}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        okText={editing ? 'Save' : 'Create'}
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please give the license a name' }]}
            extra="Identify the physical machine — e.g. 'Studio Mac mini' or 'Stage A show PC'."
          >
            <Input autoFocus />
          </Form.Item>
        </Form>
      </Modal>

      <SlideTestPanel
        open={testing !== null}
        license={testing}
        onClose={() => setTesting(null)}
      />

      <SlideConnectionsModal
        open={connectionsOf !== null}
        license={connectionsOf}
        onClose={() => setConnectionsOf(null)}
      />
    </AppLayout>
  );
}
